'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack } from '@terra/ui-styled-system';
import {
  EntityDrawerSections,
  EntityDrawerSection,
} from '../../components/entity-drawer';
import {
  TimelineRoot,
  type TimelineStepData,
  type TimelineStepBadge,
} from '../timeline';
import { Spinner } from '../../components/spinner';
import {
  BodyDefault,
  BodyStrong,
  MetaDefault,
} from '../../components/typography';
import { Badge } from '../../components/badge';
import { useBranding } from '../branding/BrandingProvider';
import {
  isGenericSystemActorName,
  isNeoActorName,
} from '../findings/tabs/finding-audit-actor';
import {
  ArchiveIcon,
  BotIcon,
  RefreshIcon,
  File01Icon,
  ComputerIcon,
  ScrollIcon,
  Settings01Icon,
  Shield01Icon,
  SparklesIcon,
  Tag01Icon,
  UserIcon,
  UserAdd01Icon,
} from '@terra/ui-icons';
import type { SignalAuditLog } from '@terra/shared-contracts-signals';
import type { ReactNode } from 'react';
import { formatCategoryForDisplay, formatEnumForDisplay } from '../../utils';
import { ExecutionNeoIcon } from '../icons/ExecutionNeoIcon';

type SignalDrawerAuditTabProps = {
  auditLogs: SignalAuditLog[];
  isLoading?: boolean;
  dateFormatter: {
    fromString: (date: string) => string;
  };
};
type AuditLogMetadata = Record<string, unknown>;
type SignalAuditTranslate = ReturnType<typeof useTranslations>;
const ACTORLESS_LIFECYCLE_ACTIONS = new Set<SignalAuditLog['action']>([
  'ARCHIVED',
  'RESTORED',
]);
const ACTION_ICONS: Record<SignalAuditLog['action'], ReactNode> = {
  CREATED: <File01Icon size={16} />,
  LABEL_CHANGED: <Tag01Icon size={16} />,
  STAGE_CHANGED: <Tag01Icon size={16} />,
  STATUS_CHANGED: <Tag01Icon size={16} />,
  RANK_CHANGED: <Shield01Icon size={16} />,
  CATEGORY_CHANGED: <Settings01Icon size={16} />,
  ASSIGNED: <UserAdd01Icon size={16} />,
  ENRICHED: <SparklesIcon size={16} />,
  ARCHIVED: <ArchiveIcon size={16} />,
  RESTORED: <RefreshIcon size={16} />,
};
const getToolName = (metadata: AuditLogMetadata): string | null => {
  if (typeof metadata.tool === 'string') return metadata.tool;

  return null;
};

const getThreadId = (metadata: AuditLogMetadata): string | null => {
  if (typeof metadata.threadId === 'string') return metadata.threadId;

  return null;
};

const formatThreadId = (threadId: string): string => {
  if (threadId.length <= 12) return threadId;

  return `${threadId.slice(0, 6)}...${threadId.slice(-4)}`;
};

const getTriggeredByUser = (
  actorType: SignalAuditLog['actorType'],
  metadata: AuditLogMetadata,
): string | null => {
  if (actorType !== 'AI') return null;
  if (typeof metadata.triggeredByUserName === 'string') {
    return metadata.triggeredByUserName;
  }

  return null;
};

const ActorIcon = ({
  actorType,
}: {
  actorType: SignalAuditLog['actorType'];
}) => {
  switch (actorType) {
    case 'AI':
      return <BotIcon size={14} />;
    case 'USER':
      return <UserIcon size={14} />;
    default:
      return <ComputerIcon size={14} />;
  }
};

const getActorName = ({
  log,
  brandName,
  t,
}: {
  log: SignalAuditLog;
  brandName: string;
  t: SignalAuditTranslate;
}): string => {
  if (isNeoActorName(log.actorName) && log.actorType !== 'USER') {
    return t('signalAudit.securityAgent');
  }

  if (log.actorType === 'SYSTEM' && isGenericSystemActorName(log.actorName)) {
    return t('signalAudit.agentActor', { brandName });
  }

  return log.actorName || t('signalAudit.unknownActor');
};

const getActorDisplay = ({
  log,
  brandName,
  t,
}: {
  log: SignalAuditLog;
  brandName: string;
  t: SignalAuditTranslate;
}): ReactNode => {
  const actorName = getActorName({ log, brandName, t });
  const metadata = (log.metadata ?? {}) as AuditLogMetadata;
  const suffix = (() => {
    if (log.actorType === 'USER') {
      const tool = getToolName(metadata);

      if (tool) {
        return (
          <MetaDefault as="span" color="fg.muted">
            {t('signalAudit.viaTool', { tool })}
          </MetaDefault>
        );
      }
    }

    if (log.actorType === 'AI') {
      const triggeredBy = getTriggeredByUser(log.actorType, metadata);
      const label = triggeredBy
        ? t('signalAudit.triggeredBy', { name: triggeredBy })
        : t('signalAudit.triggeredBy', { name: t('signalAudit.scheduler') });

      return (
        <MetaDefault as="span" color="fg.muted">
          ({label})
        </MetaDefault>
      );
    }

    return null;
  })();

  return (
    <Box display="inline-flex" alignItems="center" gap="1" flexWrap="wrap">
      <ActorIcon actorType={log.actorType} />
      <BodyDefault as="span">{actorName}</BodyDefault>
      {suffix}
    </Box>
  );
};

const formatFieldValue = (value: string, fieldName: string | null): string => {
  if (fieldName === 'category') return formatCategoryForDisplay(value);
  if (fieldName === 'assigneeId') return value;

  return formatEnumForDisplay(value);
};

const renderAuditValueBadge = ({
  value,
  fieldName,
}: {
  value: string;
  fieldName: string | null;
}): ReactNode => {
  const isLifecycleField = fieldName === 'stage' || fieldName === 'status';
  const displayValue = isLifecycleField
    ? value.toUpperCase()
    : formatFieldValue(value, fieldName);

  return (
    <Badge size="sm" variant="outline">
      {displayValue}
    </Badge>
  );
};

const getActionDescription = (
  log: SignalAuditLog,
  t: SignalAuditTranslate,
): ReactNode | undefined => {
  if (log.oldValue && log.newValue) {
    return (
      <HStack gap="2" alignItems="center" flexWrap="wrap">
        <BodyDefault as="span">{t('signalAudit.from')}:</BodyDefault>
        {renderAuditValueBadge({
          value: log.oldValue,
          fieldName: log.fieldName,
        })}
        <BodyDefault as="span">→ {t('signalAudit.to')}:</BodyDefault>
        {renderAuditValueBadge({
          value: log.newValue,
          fieldName: log.fieldName,
        })}
      </HStack>
    );
  }

  if (log.newValue) {
    return (
      <HStack gap="2" alignItems="center" flexWrap="wrap">
        <BodyDefault as="span">{t('signalAudit.to')}:</BodyDefault>
        {renderAuditValueBadge({
          value: log.newValue,
          fieldName: log.fieldName,
        })}
      </HStack>
    );
  }

  return undefined;
};

const buildThreadBadges = (
  metadata: AuditLogMetadata,
): TimelineStepBadge[] | undefined => {
  const threadId = getThreadId(metadata);

  if (!threadId) return undefined;

  return [
    {
      label: `Thread: ${formatThreadId(threadId)}`,
    },
  ];
};

const buildTimelineStep = ({
  log,
  brandName,
  t,
}: {
  log: SignalAuditLog;
  brandName: string;
  t: SignalAuditTranslate;
}): TimelineStepData => {
  const isActorless = ACTORLESS_LIFECYCLE_ACTIONS.has(log.action);
  const actorDisplay = getActorDisplay({ log, brandName, t });
  const metadata = (log.metadata ?? {}) as AuditLogMetadata;
  const threadId = getThreadId(metadata);
  const icon =
    threadId != null ? (
      <ExecutionNeoIcon size={16} />
    ) : (
      (ACTION_ICONS[log.action] ?? <Settings01Icon size={16} />)
    );
  const rightBadges = buildThreadBadges(metadata);
  const noteLabel = isActorless
    ? t('signalAudit.noteLabel')
    : t('signalAudit.noteBy', {
        actor: getActorName({ log, brandName, t }),
      });
  const normalizedNote =
    typeof log.note === 'string' && log.note.trim().length > 0
      ? log.note.trim()
      : null;
  const baseDescription = getActionDescription(log, t);
  const description = normalizedNote ? (
    <Box display="flex" flexDirection="column" gap="1">
      {baseDescription}
      <MetaDefault as="span" color="fg.muted">
        <MetaDefault as="span" fontWeight="medium" color="fg.default">
          {noteLabel}:
        </MetaDefault>{' '}
        {normalizedNote}
      </MetaDefault>
    </Box>
  ) : (
    baseDescription
  );

  if (log.action === 'CREATED') {
    return {
      id: log.id,
      title: actorDisplay,
      icon,
      details: [
        {
          timestamp: log.createdAt,
          description: t('signalAudit.actions.CREATED'),
          rightBadges,
          ...(threadId && { tooltip: threadId }),
        },
      ],
    };
  }

  const actionText = t(
    `signalAudit.actions.${log.action}` as Parameters<typeof t>[0],
  );

  return {
    id: log.id,
    title: isActorless ? (
      t(`signalAudit.titles.${log.action}` as Parameters<typeof t>[0])
    ) : (
      <Box display="inline-flex" alignItems="center" gap="1" flexWrap="wrap">
        {actorDisplay}
        <BodyDefault as="span"> {actionText}</BodyDefault>
      </Box>
    ),
    icon,
    details: [
      {
        timestamp: log.createdAt,
        description,
        rightBadges,
        ...(threadId && { tooltip: threadId }),
      },
    ],
  };
};

export const SignalDrawerAuditTab = ({
  auditLogs,
  isLoading,
  dateFormatter,
}: SignalDrawerAuditTabProps) => {
  const t = useTranslations('designSystem.applicationSignals');
  const { brandName } = useBranding();
  const timelineSteps = useMemo(
    () => auditLogs.map((log) => buildTimelineStep({ log, brandName, t })),
    [auditLogs, brandName, t],
  );

  if (isLoading) {
    return (
      <EntityDrawerSections>
        <EntityDrawerSection title={t('signalAudit.title')}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={8}
          >
            <Spinner size="lg" />
          </Box>
        </EntityDrawerSection>
      </EntityDrawerSections>
    );
  }

  if (timelineSteps.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="full"
        h="full"
        py={12}
      >
        <Box
          display="flex"
          flexDirection="column"
          gap={4}
          alignItems="center"
          maxW="md"
        >
          <ScrollIcon size={24} color="var(--colors-fg-muted)" />
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            alignItems="center"
          >
            <BodyStrong color="fg.default">
              {t('signalAudit.noLogs')}
            </BodyStrong>
            <MetaDefault color="fg.muted" textAlign="center">
              {t('signalAudit.noLogsDescription')}
            </MetaDefault>
          </Box>
        </Box>
      </Box>
    );
  }

  const timelineDateFormatter = {
    format: (date: Date) => dateFormatter.fromString(date.toISOString()),
  };

  return (
    <EntityDrawerSections>
      <EntityDrawerSection title={t('signalAudit.title')}>
        <TimelineRoot
          steps={timelineSteps}
          dateFormatter={timelineDateFormatter}
          timestampPlacement="outside"
        />
      </EntityDrawerSection>
    </EntityDrawerSections>
  );
};
